import { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useStoreReportes } from '../../paginas/Reportes/useStoreReportes';
import { useStoreHome } from '../../paginas/Home/useStoreHome';
import { enumReporteSeleccionado } from '../FuncionesLocales';
import './stockReportes.css';
import ExportarReporte from '../ExportarReporte/ExportarReporte';
import TipoProductoFiltro from '../TipoProductoFiltro/TipoProductoFiltro';

const CHART_COLOR_TOKENS = [
    ['--app-brand-deep', '#244537'],
    ['--app-brand-dark', '#315b49'],
    ['--app-brand', '#477c63'],
    ['--app-accent', '#73a98d'],
    ['--app-warning', '#946b22'],
    ['--app-danger', '#a23c35'],
    ['--app-brand-border', '#c8ddd0'],
    ['--app-brand-soft', '#e7f1eb'],
];

const leerColorTema = (styles, token, fallback) =>
    styles.getPropertyValue(token).trim() || fallback;

const obtenerPaletaGraficos = (styles) =>
    CHART_COLOR_TOKENS.map(([token, fallback]) => leerColorTema(styles, token, fallback));

export default function StockReportes() {
    const [chartsIngresos, setChartsIngresos] = useState({});
    const [chartsOtros, setChartsOtros] = useState({});
    const [chartOptions, setChartOptions] = useState({});
    const BuscandoStock = useStoreReportes((state) => state.BuscandoStock);
    const BuscandoStockExcel = useStoreReportes((state) => state.BuscandoStockExcel);
    const ListaStockExcel = useStoreReportes((state) => state.ListaStockExcel);
    const Lote = useStoreReportes((state) => state.LoteProduccion);
    const TipoProducto = useStoreReportes((state) => state.TipoProducto);

    useEffect(() => {
        useStoreHome.setState({ ReportesSeleccionado: enumReporteSeleccionado.STOCK });
        useStoreReportes.getState().getStock();
        useStoreReportes.getState().getStockExcel();
    }, [TipoProducto]);


    useEffect(() => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = leerColorTema(documentStyle, '--app-ink', '#294438');
        const textColorSecondary = leerColorTema(documentStyle, '--app-muted', '#687870');
        const surfaceBorder = leerColorTema(documentStyle, '--app-border', '#dce6df');



        // ---------- Separar por Proceso ----------
        const esProductoTerminado = (it) => it.EsProductoTerminado || it.TipoProducto === 'ProductoTerminado';

        const listaIngresos = (ListaStockExcel || []).filter((it) => !esProductoTerminado(it));
        const listaOtros = (ListaStockExcel || []).filter(esProductoTerminado);

        // --------- agrupar por unidad ----------
        const colors = obtenerPaletaGraficos(documentStyle);

        const groupByUnidad = (lista) =>
            lista.reduce((acc, item) => {
                const unidad = item.UnidadDescripcion || 'Sin unidad';
                (acc[unidad] ||= []).push(item);
                return acc;
            }, {});

        const buildCharts = (grouped) => {
            const charts = {};
            Object.keys(grouped).forEach((unidad) => {
                const unitData = grouped[unidad];

                
                const data = unitData.map((it) => it.Peso);

                charts[unidad] = {
                    labels: unitData.map((it) => it.sDescripcion || 'Sin descripción'),
                    datasets: [
                        {
                            label: `${unidad}`,
                            backgroundColor: colors,
                            borderColor: colors,
                            hoverBackgroundColor: colors,
                            data
                        }
                    ]
                };
            });
            return charts;
        };

        
        setChartsIngresos(buildCharts(groupByUnidad(listaIngresos)));
        setChartsOtros(buildCharts(groupByUnidad(listaOtros)));

        
        setChartOptions({
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    labels: { color: textColor }
                }
            },
            scales: {
                x: {
                    ticks: { color: textColorSecondary, font: { weight: 500 }, maxRotation: 90, minRotation: 0 },
                    grid: { display: true, drawBorder: true }
                },
                y: {
                    ticks: { color: textColorSecondary },
                    grid: { color: surfaceBorder, drawBorder: false }
                }
            }
        });
    }, [ListaStockExcel]);

    const renderBloque = (titulo, chartsObj) => {
        const unidades = Object.keys(chartsObj);
        if (!unidades.length) return null;
        return (
            <>
                <h2 className="stock-section-title">{titulo}</h2>
                {unidades.map((unidad) => (
                    <div key={`${titulo}-${unidad}`} style={{ marginBottom: 8 }}>
                        <h3 className="stock-chart-title">
                            Stock por {unidad}
                        </h3>
                        <Chart type="bar" data={chartsObj[unidad]} options={chartOptions} style={{ width: '100%', height: 400 }} />
                    </div>
                ))}
            </>
        );
    };

    const hayChartsIngresos = Object.keys(chartsIngresos).length > 0;
    const hayChartsOtros = Object.keys(chartsOtros).length > 0;
    const cantidadBloques = Number(hayChartsIngresos) + Number(hayChartsOtros);
    const buscandoDatos = BuscandoStock || BuscandoStockExcel;

    return (
        <div className="card">
            <div className="reportes-exportacion">
                <TipoProductoFiltro />
                <ExportarReporte endpoint="PISeunReportes/Stock" nombre="Stock" filters={{ Lote: Lote || "", TipoProducto }} disabled={buscandoDatos} />
            </div>
            {buscandoDatos ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" />
                </div>
            ) : (
                <div className="stock-wrapper">
                    <div className={`stock-grid${cantidadBloques === 1 ? ' stock-grid--single' : ''}`}>
                        {hayChartsIngresos && (
                            <div className="stock-col">
                                {renderBloque('Materias Primas / Insumos', chartsIngresos)}
                            </div>
                        )}
                        {hayChartsOtros && (
                            <div className="stock-col">
                                {renderBloque('Productos Terminados', chartsOtros)}
                            </div>
                        )}
                        {!cantidadBloques && (
                            <div className="stock-empty">
                                <h3>📈 Sin datos de stock disponibles</h3>
                                <p>No hay información de stock para mostrar</p>
                            </div>
                        )}
                    </div>
                </div>

            )}
        </div>
    );
}


